<template>
  <v-sheet :color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'">
    <v-container class="main-container mt-7">
      <!-- Title -->
      <v-row dense>
        <v-col cols="12">
          <div class="titleName mb-5 mt-3 d-flex align-center">
            <v-img
              class="mr-3"
              max-width="18"
              src="@/assets/Dashboard/bar.svg"
            />
            <span>Recent Transactions</span>
          </div>
        </v-col>
      </v-row>

      <!-- Card -->
      <v-row dense>
        <v-col cols="12">
          <v-card
            elevation="0"
            class="rounded-0"
            :color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'"
          >
            <!-- See all link -->
            <div class="commission-settings-link see-all-deposit-link">
              <router-link v-if="linkText" class="links" :to="linkUrl">
                {{ linkText }}
              </router-link>
            </div>

            <!-- TABS -->
            <v-tabs
              v-model="activeTab"
              :background-color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'"
              class="pa-0 commission-settings-tabs"
            >
              <v-tab v-if="hasDepositAccess">
                Deposit
                <strong>{{ formattedAllNumber(totalDepositCount) }}</strong>
              </v-tab>

              <v-tab v-if="hasWithdrawPageAccess">
                Withdraw
                <strong>{{ formattedAllNumber(totalWithdrawalCount) }}</strong>
              </v-tab>

              <v-tab v-if="hasIntxPageAccess"> Internal Trx </v-tab>

              <v-tab v-if="partnerPageAccess"> Partners Trx </v-tab>
            </v-tabs>

            <!-- TAB CONTENT -->
            <v-tabs-items v-model="activeTab">
              <v-tab-item v-if="hasDepositAccess">
                <v-card
                  flat
                  class="pt-5"
                  :color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'"
                >
                  <DepositTabTable />
                </v-card>
              </v-tab-item>

              <v-tab-item v-if="hasWithdrawPageAccess">
                <v-card
                  flat
                  class="pt-5"
                  :color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'"
                >
                  <WithdrawTabTable />
                </v-card>
              </v-tab-item>

              <v-tab-item v-if="hasIntxPageAccess">
                <v-card
                  flat
                  class="pt-5"
                  :color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'"
                >
                  <InternalTransferTabTable />
                </v-card>
              </v-tab-item>

              <v-tab-item v-if="partnerPageAccess">
                <v-card
                  flat
                  class="pt-5"
                  :color="$vuetify.theme.dark ? '#162334' : '#E8F1F8'"
                >
                  <PartnerTransferTabTable />
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-sheet>
</template>

<script>
import DepositTabTable from "./DepositTabTable.vue";
import WithdrawTabTable from "./WithdrawTabTable.vue";
import InternalTransferTabTable from "./InternalTransferTabTable.vue";
import PartnerTransferTabTable from "./PartnerTransferTabTable.vue";

import { mapState, mapActions } from "vuex";
import {
  TRANSACTIONS__ACTIONS,
  PARTNERWITHDRAW__ACTIONS,
} from "@/store/action-types";
import { formatNumber } from "@/_helpers/formatNumber";

export default {
  components: {
    DepositTabTable,
    WithdrawTabTable,
    InternalTransferTabTable,
    PartnerTransferTabTable,
  },

  data() {
    return {
      activeTab: 0,
      hasLoadedWithdrawals: false,
      hasLoadedInternal: false,
      hasLoadedPartners: false,
    };
  },

  computed: {
    ...mapState("trxdeposit", ["totalDepositCount"]),
    ...mapState("trxwithdrawal", ["totalWithdrawalCount"]),

    hasDepositAccess() {
      return this.$canRoute("deposit") > 0;
    },
    hasWithdrawPageAccess() {
      return this.$canRoute("withdraw") > 0;
    },
    hasIntxPageAccess() {
      return this.$canRoute("internal-transctions") > 0;
    },
    partnerPageAccess() {
      return this.$canRoute("withdraw-requests") > 0;
    },

    visibleTabs() {
      const tabs = [];
      if (this.hasDepositAccess) tabs.push("deposit");
      if (this.hasWithdrawPageAccess) tabs.push("withdraw");
      if (this.hasIntxPageAccess) tabs.push("internal-transctions");
      if (this.partnerPageAccess) tabs.push("withdraw-requests");
      return tabs;
    },

    activeTabType() {
      return this.visibleTabs[this.activeTab] || null;
    },

    linkText() {
      const map = {
        deposit: "See All Deposit >",
        withdraw: "See All Withdraw >",
        "internal-transctions": "See All Internal Transfers >",
        "withdraw-requests": "See All Partner Transfers >",
      };
      return map[this.activeTabType] || "";
    },

    linkUrl() {
      const map = {
        deposit: "/deposit",
        withdraw: "/withdraw",
        "internal-transctions": "/internal-transctions",
        "withdraw-requests": "/withdraw-requests",
      };
      return map[this.activeTabType] || "#";
    },
  },

  methods: {
    ...mapActions("trxdeposit", [TRANSACTIONS__ACTIONS.TRXDASHBOARDDEPOSITS]),
    ...mapActions("trxwithdrawal", [
      TRANSACTIONS__ACTIONS.TRXDASHBOARDWITHDRAWAL,
    ]),
    ...mapActions("trxinternal", [
      TRANSACTIONS__ACTIONS.TRXDASHBOARDINTERNALTRANSFER,
    ]),
    ...mapActions("partnerwithdraw", [
      PARTNERWITHDRAW__ACTIONS.DASHBOARDPARTNERWITHDRAW,
    ]),

    formattedAllNumber(num) {
      return formatNumber(num || 0);
    },
  },

  watch: {
    activeTab() {
      const tabType = this.activeTabType;
      if (!tabType) return;

      if (tabType === "withdraw" && !this.hasLoadedWithdrawals) {
        this[TRANSACTIONS__ACTIONS.TRXDASHBOARDWITHDRAWAL]();
        this.hasLoadedWithdrawals = true;
      }

      if (tabType === "internal-transctions" && !this.hasLoadedInternal) {
        this[TRANSACTIONS__ACTIONS.TRXDASHBOARDINTERNALTRANSFER]();
        this.hasLoadedInternal = true;
      }

      if (tabType === "withdraw-requests" && !this.hasLoadedPartners) {
        this[PARTNERWITHDRAW__ACTIONS.DASHBOARDPARTNERWITHDRAW]();
        this.hasLoadedPartners = true;
      }
    },

    visibleTabs: {
      immediate: true,
      handler(newTabs) {
        if (!newTabs.includes(this.activeTabType)) {
          this.activeTab = 0;
        }
      },
    },
  },
};
</script>
